import { useEffect, useState } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import { BlockIcon, BlockType } from "@/components/PowerBlock/Blocks";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dataset, Evaluation } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { displayErrorToast } from "@/utils/toast";

interface NameEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "workflow" | "prompt" | "dataset" | "evaluation";
  onConfirm: (name: string) => void;
}

const getEntityConfig = (entityType: NameEntityDialogProps["entityType"]) => {
  // eslint-disable-next-line default-case
  switch (entityType) {
    case "workflow":
      return {
        title: "Name your workflow",
        placeholder: "Enter workflow name...",
        icon: BlockIcon[BlockType.WORKFLOW],
      };
    case "prompt":
      return {
        title: "Name your prompt",
        placeholder: "Enter prompt name...",
        icon: BlockIcon[BlockType.PROMPT_TEMPLATE],
      };
    case "dataset":
      return {
        title: "Name your dataset",
        placeholder: "Enter dataset name...",
        icon: Dataset, // Replace with appropriate icon
      };
    case "evaluation":
      return {
        title: "Name your evaluation",
        placeholder: "Enter evaluation name...",
        icon: Evaluation, // Replace with appropriate icon
      };
  }
};

export const NameEntityDialog: React.FC<NameEntityDialogProps> = ({
  open,
  onOpenChange,
  entityType,
  onConfirm,
}) => {
  const [entityName, setEntityName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const config = getEntityConfig(entityType);
  const Icon = config.icon;

  useEffect(() => {
    if (open) {
      setEntityName("");
      setIsCreating(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!entityName.trim()) {
      return;
    }

    setIsCreating(true);

    try {
      onConfirm(entityName.trim());
      onOpenChange(false);
    } catch {
      displayErrorToast("Error creating entity");
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
              <Icon className="h-4 w-4 text-blue-500" />
            </div>
            {config.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="entity-name">Name</Label>
            <Input
              id="entity-name"
              type="text"
              value={entityName}
              onChange={e => setEntityName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={config.placeholder}
              className="w-full"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isCreating}
            />
          </div>

          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-sm text-gray-600">You can always change the name later.</p>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!entityName.trim() || isCreating}
            className="min-w-[120px]"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="4" className="mr-0" />
                Creating...
              </span>
            ) : (
              `Create ${entityType}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NameEntityDialog;
